import { ProteinFormulationVariantIngredientDTO } from 'business/models/Models';
import RMTable from 'components/customComponents/Controls/RMTable';
import { RMModal } from 'components/modal';
import { FmlxButton, FmlxIcon } from 'fmlx-common-ui';
import React, { useMemo, useRef, useState } from 'react';
import { getNewId, isNullOrUndefined } from 'utils/Global';
import {
  useAddVariantIngredient,
  useCreateProteinFormulationVariants,
  useDeleteVariantIngredient,
  useProteinFormulationData,
  useTableColumns,
  useTableData,
} from './hooks';
import ImportIngredientError from './ImportIngredientError';
import ProteinVariantHeader from './ProteinVariantHeader';
import ProteinVariantIngredientCard from './ProteinVariantIngredientCard';
import ProteinVariantStdIngCard from './ProteinVariantStdIngCard';
import { calculateMolecularWeightAndConcentration } from './Utils';
import { ProteinIngredientsTemplate } from '../ProteinIngredientsTemplate';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';

const proteinDimensions = ['molecularWeight', 'concentrationmgmL'];
const ProteinVariantView = (props) => {
  const {
    setShowModal,
    parentProteinTreeNodeId,
    setRefreshVariantList,
    refreshVariantList,
    proteinFormulationVariants,
  } = props;
  const [selectedProteinIngs, setSelectedProteinIngs] = useState<
    ProteinFormulationVariantIngredientDTO[]
  >([]);
  const { pfData, pfIngredientTypeOptions, pfIngredientTypes } =
    useProteinFormulationData(parentProteinTreeNodeId);
  const [editId, setEditId] = useState<number>(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [proteinVariantIngredients, setProteinVariantIngredients] = useState<
    ProteinFormulationVariantIngredientDTO[]
  >([]);
  const inputFileRef = useRef(null);
  const isImportDisabled =
    useSystemProperty(PropertyConstants.ProteinImportType, 'Formulatrix') === 'None';
  const [showImportError, setShowImportError] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [pfErrors, setPFError] = useState<string>('');
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isError, nameErrors] = useMemo(() => {
    const seen = new Set();
    const duplicates = [];

    for (const item of proteinVariantIngredients) {
      if (!item.name) {
        continue;
      }
      const key = `${item.name}-${item.type}`;
      if (seen.has(key)) {
        duplicates.push(item.id);
      } else {
        seen.add(key);
      }
    }

    return [duplicates.length > 0, duplicates];
  }, [proteinVariantIngredients]);

  const { createProteinFormulationVariants } = useCreateProteinFormulationVariants(
    proteinVariantIngredients,
    pfData,
    validatePFData,
    setShowModal,
    setRefreshVariantList,
    refreshVariantList,
  );
  const { onAddVariantIngredient } = useAddVariantIngredient(
    pfIngredientTypeOptions,
    proteinVariantIngredients,
    setProteinVariantIngredients,
    isError,
    setSelectedIds,
    setIsDirty,
    setEditId,
    setIsSelectAllChecked,
  );
  const { onDeleteVariantIngredient } = useDeleteVariantIngredient(
    proteinVariantIngredients,
    setProteinVariantIngredients,
    selectedProteinIngs,
    setSelectedProteinIngs,
    setIsSelectAllChecked,
  );
  const onChangeHeaderCheckbox = (e) => {
    if (e.target.checked === true) {
      setIsSelectAllChecked(true);
      setSelectedProteinIngs([...proteinVariantIngredients]);
    } else {
      setIsSelectAllChecked(false);
      setSelectedProteinIngs([]);
    }
  };
  const columns = useTableColumns(onChangeHeaderCheckbox, isSelectAllChecked);
  const onFieldFocus = (e, id, key) => {
    const index = proteinVariantIngredients.findIndex((ing) => ing.id == id);
    setSelectedIds([index]);
  };
  const tableData = useTableData(
    proteinVariantIngredients,
    isSelected,
    onCellChange,
    onFieldFocus,
    pfIngredientTypeOptions,
    nameErrors,
    selectedProteinIngs,
  );

  const onDiscardButtonClick = async () => {
    setShowModal(false);
    setIsDirty(false);
  };

  function validatePFData() {
    let isValid = true;

    for (let i = 0; i < proteinVariantIngredients.length; i++) {
      const pfIngName = proteinVariantIngredients[i].name;
      if (isNullOrUndefined(pfIngName) || pfIngName == '') {
        isValid = false;
        proteinVariantIngredients[i].name = '';
        setPFError('Ingredient Name is a required field.');
      }
    }
    if (!isValid) {
      setShowError(true);
    }

    const newPfNames: string[] = [];
    proteinVariantIngredients.forEach((ing) => {
      const newPfName = pfData.treeNode.name + ' - ' + ing.name;
      if (proteinFormulationVariants.some((pv) => pv.treeNode.name == newPfName)) {
        newPfNames.push(newPfName);
      }
    });
    if (newPfNames.length > 0) {
      isValid = false;
      setPFError(`Protein variant with name ${newPfNames.join(', ')} already exists.`);
      setShowError(true);
    }

    return isValid;
  }

  function isSelected(id) {
    return selectedProteinIngs.find((ing) => ing.id === id) ? true : false;
  }

  function onCellChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    id,
    key,
  ) {
    const { target } = e;
    const { type, value } = target;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      if (target.checked === true) {
        const index = proteinVariantIngredients.findIndex((ing) => ing.id == id);
        selectedProteinIngs.push(proteinVariantIngredients[index]);
        setSelectedIds([index]);
      } else {
        const index = selectedProteinIngs.findIndex((ing) => ing.id == id);
        if (index >= 0) {
          selectedProteinIngs.splice(index, 1);
        }
      }
      setSelectedProteinIngs([...selectedProteinIngs]);
    } else {
      const index = proteinVariantIngredients.findIndex((ing) => ing.id == id);
      if (type === 'number' || type === 'select-one') {
        proteinVariantIngredients[index][key] = parseInt(value);
      } else {
        proteinVariantIngredients[index][key] = value;
      }
      if (proteinDimensions.includes(key)) {
        calculateMolecularWeightAndConcentration(proteinVariantIngredients[index], key);
      }
      setSelectedIds([index]);
      setProteinVariantIngredients([...proteinVariantIngredients]);
    }
  }

  const handleOnRowClick = (row) => {
    setSelectedIds([row.index]);
    if (!isError) {
      if (row.original.id != editId) {
        setEditId(undefined);
      }
    }
  };

  function handleImportIng() {
    if (inputFileRef) {
      inputFileRef.current.click();
    }
  }

  function fileChangedHandler(e: React.ChangeEvent<HTMLInputElement>) {
    let result;
    const file = e.target.files[0];
    const reader = new FileReader();
    const PFPROPNAMES = {
      0: 'type',
      1: 'name',
      2: 'molecularWeight',
      3: 'concentrationmgmL',
      4: 'concentrationmM',
    };
    reader.onload = function (event) {
      result = event.target.result;
      try {
        const pfIngs = [];
        const ings = result.split(/\r?\n/);
        if (ings.length <= 1) {
          throw new Error('Error in format');
        } else {
          ings.shift();
        }
        ings.map((ing) => {
          const props = ing.split('\t');
          if (props.length != 5) {
            throw new Error('Error in format');
          }

          const pfIng: ProteinFormulationVariantIngredientDTO =
            new ProteinFormulationVariantIngredientDTO();

          props.map((prop, i) => {
            const key = PFPROPNAMES[i];
            if (key === 'type') {
              const pfType = pfIngredientTypes?.find((pfit) => pfit.name === prop)?.id;
              if (pfType !== undefined) {
                pfIng[key] = pfType;
              } else {
                setErrorMessage('Protein type specified in the file does not exist.');
                throw new Error();
              }
            } else if (key === 'name') {
              pfIng[key] = prop;
            } else {
              pfIng[key] = parseFloat(prop);
            }
          });

          pfIng.id = getNewId();
          pfIngs.push(pfIng);
        });

        const variantIngredients = [...proteinVariantIngredients];

        pfIngs.forEach((importedIng) => {
          const newDataItem = new ProteinFormulationVariantIngredientDTO({
            id: importedIng.id,
            type: importedIng.type,
            userId: '',
            lot: '',
            name: importedIng.name,
            molecularWeight: importedIng.molecularWeight,
            concentrationmgmL: importedIng.concentrationmgmL,
            concentrationmM: importedIng.concentrationmM,
            concentrationPercent: undefined,
            pH: undefined,
          });

          variantIngredients.push(newDataItem);
        });
        setProteinVariantIngredients(variantIngredients);
        setIsDirty(true);
      } catch (e) {
        setShowImportError(true);
        console.error(e);
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  }

  return (
    <>
      <div
        id="proteinvariantview-view-wrapper"
        className="protein-variant-view-container"
      >
        <ProteinVariantHeader
          id="proteinvariantview-view-header-controls"
          setShowModal={setShowModal}
          saveVariants={createProteinFormulationVariants}
          isDirty={isDirty}
          onDiscardButtonClick={onDiscardButtonClick}
          isError={isError}
        />

        <div id="proteinvariantview-view-content" className="protein-variant-view-body">
          <div id="proteinvariantview-view-leftpanel" className="protein-details">
            <div
              id="proteinvariantview-view-leftpanel-header"
              className="protein-details-header"
            >
              <div
                id="proteinvariantview-view-header-title"
                className="protein-details-header-title"
              >
                Selected Protein Details
              </div>
              <div
                id={`proteinvariantview-view-selectedprotein-name-${pfData?.treeNode?.name}`}
                className="protein-details-header-name"
              >
                {pfData?.treeNode?.name}
              </div>
            </div>
            <div
              id="proteinvariantview-view-leftpanel-ingredients"
              className="protein-details-content"
            >
              <div
                id="proteinvariantview-view-proteining-col"
                className="protein-details-content-protein"
              >
                <div
                  id="proteinvariantview-view-proteining-title"
                  className="protein-details-content-protein-header"
                >
                  Protein
                </div>
                <div
                  id="proteinvariantview-view-proteining-list"
                  className="protein-details-content-protein-content"
                >
                  {pfData?.proteinFormulationIngredient?.map((ingredient, index) => (
                    <ProteinVariantIngredientCard
                      id={`proteinvariantview-view-proteining-card-${index}`}
                      key={`proteinVariantIngredientCard${index}`}
                      ingredient={ingredient}
                    />
                  ))}
                </div>
              </div>
              <div
                id="proteinvariantview-view-standarding-col"
                className="protein-details-content-ingredients"
              >
                <div
                  id="proteinvariantview-view-standarding-title"
                  className="protein-details-content-ingredients-header"
                >
                  Standard Ingredients
                </div>
                <div
                  id="proteinvariantview-view-standarding-list"
                  className="protein-details-content-ingredients-content"
                >
                  {pfData?.proteinFormulationStandardIngredient?.map(
                    (ingredient, index) => (
                      <ProteinVariantStdIngCard
                        id={`proteinvariantview-view-stding-card-${index}`}
                        key={`proteinVariantStdIngCard${index}`}
                        ingredient={ingredient}
                      />
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
          <div id="proteinvariantview-view-rightpanel" className="variable-protein">
            <div
              id="proteinvariantview-view-rightpanel-header"
              className="variable-protein-header"
            >
              <div
                id="proteinvariantview-view-rightpanel-title"
                className="variable-protein-header-title"
              >
                Variable Protein
              </div>
              <div
                id="proteinvariantview-view-rightpanel-description"
                className="variable-protein-header-desc"
              >
                You can choose multiple ingredients here to mixed with your existing
                protein to be a new Protein Variant.
              </div>
            </div>
            <div
              id="proteinvariantview-rightpanel-actionbar"
              className="variable-protein-action-container"
            >
              <div
                id="proteinvariantview-rightpanel-actionbar-label"
                className="variable-protein-action-title"
              >
                Protein
              </div>
              <ProteinIngredientsTemplate width="100%" />
              <div
                id="proteinvariantview-rightpanel-actionbar-buttons"
                className="variable-protein-action-button"
              >
                <FmlxButton
                  id="proteinvariantview-rightpanel-button-import"
                  onClick={handleImportIng}
                  label="IMPORT"
                  tooltipDescription="Import Ingredients"
                  withTooltip
                  disabled={isImportDisabled}
                  type={FmlxButton.Type.BASIC}
                  variant={FmlxButton.Variant.OUTLINE}
                />
                <input
                  id="proteinvariantview-rightpanel-button-ingredient-import-input"
                  type="file"
                  ref={inputFileRef}
                  className="d-none"
                  onChange={fileChangedHandler}
                  accept=".txt"
                />
                <FmlxButton
                  id="proteinvariantview-rightpanel-button-add"
                  onClick={onAddVariantIngredient}
                  onlyIcon
                  icon={<FmlxIcon name="AddCircle" />}
                  tooltipDescription={'Add'}
                  withTooltip
                  tooltipPlacement={FmlxButton.TooltipPlacement.BOTTOM}
                  type={FmlxButton.Type.POSITIVE}
                />
                <FmlxButton
                  id="proteinvariantview-rightpanel-button-delete"
                  onClick={onDeleteVariantIngredient}
                  onlyIcon
                  icon={<FmlxIcon name="Trash" />}
                  tooltipDescription={'Delete'}
                  withTooltip
                  tooltipPlacement={FmlxButton.TooltipPlacement.BOTTOM}
                  type={FmlxButton.Type.DANGER}
                />
              </div>
            </div>
            <div
              id="proteinvariantview-rightpanel-table-wrapper"
              className="variable-protein-table-container"
            >
              <RMTable
                columns={columns}
                data={tableData}
                tableClass="w-[100%] h-[100%]"
                enableMultiSelection={false}
                selectedIds={selectedIds}
                onRowClicked={handleOnRowClick}
              />
            </div>
          </div>
        </div>
      </div>
      <ImportIngredientError
        showImportError={showImportError}
        setShowImportError={setShowImportError}
        errorMessage={errorMessage}
      />
      <RMModal
        id="proteinvariantview-view-modal-error"
        showModal={showError}
        setShowModal={setShowError}
        onOk={() => setShowError(false)}
        body={pfErrors}
        heading="Error"
        okButtonText="OK"
        isOkOnly
        titleClr="#DB0000"
      />
    </>
  );
};

export default ProteinVariantView;
